/**
 * A selectable node in the Scan step's tree — a chat, a mail folder, a
 * calendar, a notebook section. Pure data; the UI (scan-panel.ts) turns it
 * into checkboxes, and the Graph scan (scan.ts) produces it.
 */
export interface ScanNode {
    id: string;
    label: string;
    /** A short muted detail, e.g. a chat type or an item count. */
    hint?: string;
    children: ScanNode[];
}

export interface FlatItem {
    id: string;
    label: string;
    parentId?: string;
    hint?: string;
}

/**
 * Builds a forest from a flat parent-linked list (Graph returns mail folders
 * this way — each carries a parentFolderId). An item whose parent is not in
 * the set is a root, which is the normal case for a top-level folder whose
 * true parent (msgfolderroot) is never returned. Children are sorted by label
 * for a stable display; the same reconciliation Mail's own path resolver uses.
 *
 * Duplicate ids are collapsed to a single node. This matters because Graph's
 * `/delta` feeds (OneDrive folders, mail folders) are documented to return the
 * same item more than once — without deduping, every repeat pushed another
 * copy of the node onto its parent, duplicating whole subtrees in the tree.
 */
export function buildForest(items: readonly FlatItem[]): ScanNode[] {
    const entries = new Map<string, { node: ScanNode; parentId?: string }>();
    for (const item of items) {
        if (entries.has(item.id)) {
            continue; // first occurrence wins; a repeat is the same folder
        }
        entries.set(item.id, {
            node: {
                id: item.id,
                label: item.label,
                ...(item.hint ? { hint: item.hint } : {}),
                children: []
            },
            ...(item.parentId ? { parentId: item.parentId } : {})
        });
    }

    const roots: ScanNode[] = [];
    for (const { node, parentId } of entries.values()) {
        const parent = parentId ? entries.get(parentId)?.node : undefined;
        if (parent && parent !== node) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    }

    const sortRecursive = (nodes: ScanNode[]): void => {
        nodes.sort((a, b) => a.label.localeCompare(b.label));
        for (const node of nodes) {
            sortRecursive(node.children);
        }
    };
    sortRecursive(roots);
    return roots;
}

/** Every id in a forest, roots and descendants. */
export function collectIds(nodes: readonly ScanNode[]): string[] {
    const ids: string[] = [];
    const walk = (list: readonly ScanNode[]): void => {
        for (const node of list) {
            ids.push(node.id);
            walk(node.children);
        }
    };
    walk(nodes);
    return ids;
}
